import axios from 'axios';
import { getToken } from './auth';

// Definindo a URL base da API do backend
const API_URL = process.env.REACT_APP_API_URL || 'https://api.planmyplate.io';

// Configurando o axios para incluir automaticamente o token de autenticação
axios.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export interface UpgradeUserPlanRequest {
  user_id: string;
  plan: 'pro' | 'premium';
  billing: 'monthly' | 'yearly';
  duration_days?: number;
  reason?: string;
  notify_user?: boolean;
}

export interface DowngradeUserPlanRequest {
  user_id: string;
  action: 'downgrade_to_free' | 'cancel_immediate' | 'cancel_at_period_end';
  reason?: string;
  notify_user?: boolean;
}

export interface UserRefundRequest {
  user_id: string;
  amount?: number; // Optional - if not provided, refunds the full amount
  reason: string;
  refund_type: 'full' | 'partial';
  notify_user?: boolean;
  admin_notes?: string;
}

export interface UserDiscountRequest {
  user_id: string;
  discount_code?: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  expires_at?: string;
  description?: string;
  notify_user?: boolean;
}

export interface SubscriptionResponse {
  success: boolean;
  message?: string;
  error?: string;
  user?: {
    id: string;
    name: string;
    email: string;
    subscription: any;
    total_spent?: number;
    total_refunds?: number;
  };
  action?: string;
}

export interface RefundResponse {
  success: boolean;
  message?: string;
  error?: string;
  refund?: {
    id: string;
    amount: number;
    type: string;
    status: string;
    reason: string;
    created_at: string;
    stripe_refund_id?: string;
  };
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    subscription: any;
    total_spent: number;
    total_refunds: number;
  };
}

export interface SubscriptionHistoryResponse {
  success: boolean;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  current_subscription?: any;
  history?: any[];
  error?: string;
}

class AdminSubscriptionService {
  /**
   * Upgrade user's subscription plan
   */
  async upgradeUserPlan(data: UpgradeUserPlanRequest): Promise<SubscriptionResponse> {
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/upgrade-plan`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error upgrading user plan:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to upgrade user plan'
      };
    }
  }

  /**
   * Downgrade or cancel user's subscription
   */
  async downgradeUserPlan(data: DowngradeUserPlanRequest): Promise<SubscriptionResponse> {
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/downgrade-plan`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error downgrading user plan:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to downgrade user plan'
      };
    }
  }

  /**
   * Get user's subscription history
   */
  async getUserSubscriptionHistory(userId: string): Promise<SubscriptionHistoryResponse> {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/subscription-history`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching subscription history:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch subscription history'
      };
    }
  }

  /**
   * Get available subscription plans
   */
  async getSubscriptionPlans(): Promise<any> {
    try {
      const response = await axios.get(`${API_URL}/api/payments/subscription-plans`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching subscription plans:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch subscription plans'
      };
    }
  }

  /**
   * Apply discount to user
   */
  async applyUserDiscount(data: UserDiscountRequest): Promise<SubscriptionResponse> {
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/apply-discount`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error applying user discount:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to apply discount'
      };
    }
  }

  /**
   * Remove discount from user
   */
  async removeUserDiscount(userId: string): Promise<SubscriptionResponse> {
    try {
      const response = await axios.delete(`${API_URL}/api/admin/users/${userId}/discount`);
      return response.data;
    } catch (error: any) {
      console.error('Error removing user discount:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to remove discount'
      };
    }
  }

  /**
   * Delete user account permanently
   */
  async deleteUser(userId: string): Promise<SubscriptionResponse> {
    try {
      const response = await axios.delete(`${API_URL}/api/admin/users/${userId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting user:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete user'
      };
    }
  }

  /**
   * Send password reset email to user
   */
  async resetUserPassword(userId: string, email: string): Promise<SubscriptionResponse> {
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/${userId}/reset-password`, {
        email: email
      });
      return response.data;
    } catch (error: any) {
      console.error('Error sending password reset:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send password reset email'
      };
    }
  }

  /**
   * Process refund for user
   */
  async processUserRefund(data: UserRefundRequest): Promise<RefundResponse> {
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/process-refund`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error processing user refund:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to process refund'
      };
    }
  }
}

export const adminSubscriptionService = new AdminSubscriptionService();
export default adminSubscriptionService;
